import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { useNotificationStore } from "../../stores/notification-store";

/** 알림 확인 주기(헤더 도트·알림함 공통). Bearer 인증이라 SSE(EventSource) 대신 폴링한다. */
export const NOTIFICATION_POLL_INTERVAL_MS = 10000;

interface NotificationBellProps {
  companyId: number;
}

/**
 * 우측 상단 알림 아이콘. 읽지 않은 알림이 있으면 우측 상단에 도트를 띄운다.
 * 누르면 알림함으로 이동한다.
 */
export const NotificationBell: React.FC<NotificationBellProps> = ({ companyId }) => {
  const navigate = useNavigate();
  const unreadCount = useNotificationStore((state) => state.unreadCount);
  const refreshUnreadCount = useNotificationStore((state) => state.refreshUnreadCount);

  useEffect(() => {
    const tick = () => {
      if (document.visibilityState === "hidden") return;
      void refreshUnreadCount();
    };
    tick();
    const timer = window.setInterval(tick, NOTIFICATION_POLL_INTERVAL_MS);
    document.addEventListener("visibilitychange", tick);
    window.addEventListener("focus", tick);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", tick);
      window.removeEventListener("focus", tick);
    };
  }, [refreshUnreadCount]);

  const hasUnread = unreadCount > 0;

  return (
    <button
      type="button"
      onClick={() => navigate(`/company/${companyId}/alarms`)}
      className="relative flex items-center justify-center w-8 h-8 rounded-full text-[#757575] hover:text-[#4285F4] hover:bg-[#F5F8FF] transition-colors"
      aria-label={hasUnread ? `알림 (읽지 않은 알림 ${unreadCount}개)` : "알림"}
      title="알림"
    >
      <Bell className="w-[18px] h-[18px]" strokeWidth={1.8} />
      {hasUnread && (
        <span
          aria-hidden="true"
          className="absolute top-[5px] right-[6px] w-[7px] h-[7px] rounded-full bg-[#4285F4] ring-2 ring-white"
        />
      )}
    </button>
  );
};
